import React, { ChangeEvent, KeyboardEvent, JSX } from 'react';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { DropdownType } from '../../../types';
import styles from '../TagAutocompleteDropdown.module.scss';
import CreateButton from './CreateButton';

interface RenderDropdownContentProps {
  isCreatingMode: boolean;
  searchValue: string;
  handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  dropdownType: DropdownType;
  handleKeyDown: (e: KeyboardEvent) => void;
  confirmNewValue: () => void;
  cancelNewValue: () => void;
  filteredOptions: string[];
  localOptions: string[];
  RenderEmptyState: () => JSX.Element;
  handleOptionSelect: (option: string) => void;
  isOptionSelected: (option: string) => boolean;
  handleCheckboxChange: (e: ChangeEvent<HTMLInputElement>, option: string) => void;
  showCreateOption: boolean;
  handleCreateNew: () => void;
  createInputRef: React.RefObject<HTMLInputElement>;
}

const RenderDropdownContent = ({
  isCreatingMode,
  searchValue,
  handleSearchChange,
  dropdownType,
  handleKeyDown,
  confirmNewValue,
  cancelNewValue,
  filteredOptions,
  localOptions,
  RenderEmptyState,
  handleOptionSelect,
  isOptionSelected,
  handleCheckboxChange,
  showCreateOption,
  handleCreateNew,
  createInputRef,
}: RenderDropdownContentProps): JSX.Element => {
  if (filteredOptions.length === 0 && localOptions.length === 0) {
    return RenderEmptyState();
  }

  return (
    <>
      <div className={styles.searchBar}>
        <GenerateIcon iconName={ICONS.search.name} />
        <input
          type="text"
          placeholder="Search"
          value={searchValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      </div>

      <div className={styles.optionsList}>
        {filteredOptions.length === 0 ? (
          <div className={styles.createNewOption}>
            <div className={styles.newValueInputContainer}>
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={`Enter ${dropdownType === DropdownType.KEY ? 'Key Name' : 'Value'}`}
                className={styles.newValueInput}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
            <div className={styles.actionButtons}>
              <button className={styles.confirmButton} onClick={confirmNewValue} disabled={!searchValue}>
                <GenerateIcon iconName={ICONS.check.name} />
              </button>
              <button className={styles.cancelButton} onClick={cancelNewValue}>
                <GenerateIcon iconName={ICONS.xMark.name} />
              </button>
            </div>
          </div>
        ) : (
          filteredOptions.map((option) => (
            <div key={option} className={styles.option} onClick={() => handleOptionSelect(option)}>
              {dropdownType === DropdownType.VALUE && (
                <span className={styles.checkboxIcon}>
                  <input
                    type="checkbox"
                    checked={isOptionSelected(option)}
                    onChange={(e) => handleCheckboxChange(e, option)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </span>
              )}
              <span>{option}</span>
            </div>
          ))
        )}
      </div>

      {showCreateOption && filteredOptions.length > 0 && isCreatingMode && (
        <div className={styles.createNewContainer}>
          <div className={styles.createNewInputBox}>
            <input
              ref={createInputRef}
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={`Enter ${dropdownType === DropdownType.KEY ? 'Key' : 'Value'} Name`}
              className={styles.createNewInput}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>
          <div className={styles.createActionButtons}>
            <button className={styles.confirmButton} onClick={confirmNewValue} disabled={!searchValue}>
              <GenerateIcon iconName={ICONS.check.name} />
            </button>
            <button className={styles.cancelButton} onClick={cancelNewValue}>
              <GenerateIcon iconName={ICONS.xMark.name} />
            </button>
          </div>
        </div>
      )}
      {showCreateOption && filteredOptions.length > 0 && !isCreatingMode ? (
        <CreateButton handleCreateNew={handleCreateNew} dropdownType={dropdownType} />
      ) : null}
    </>
  );
};

export default RenderDropdownContent;
